import { Component, createContext, useContext, useState, useEffect, useLayoutEffect, useRef, memo, useMemo, useCallback, isValidElement, type ReactNode } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import type { PluggableList } from "unified";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { common } from "lowlight";
import powershell from "highlight.js/lib/languages/powershell";
import "highlight.js/styles/github-dark.css";
import { Copy, Check, Download, ExternalLink, ChevronDown, ChevronUp, Code2, Maximize2, ExternalLink as NewTab, FileText, ImageOff } from "lucide-react";
import { openHtmlInSandboxedTab } from "@/lib/sandboxed-html";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { workflowsApi } from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { withWorkspaceContext } from "@/lib/workspace-navigation";
import { remarkUuidLinks, UUID_LINK_PREFIX } from "@/lib/remark-uuid-links";
import { fileRefFromLink, isAgentFileLink, type Artifact } from "@/components/chat/artifact-detector";
import { ZoomViewer, ZoomableImage } from "@/components/chat/zoom-viewer";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/theme-provider";

// ── Chart.js (tree-shaken registration) ────────────────────────────
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Pie, Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
);

// ── Chart type set ─────────────────────────────────────────────────
const CHART_LANGUAGES = new Set(["chart-pie", "chart-bar", "chart-doughnut", "chart-line"]);

// ── Inline Chart component ─────────────────────────────────────────

// Text and grid colours per app theme. Chart.js draws on a canvas, so it cannot
// read the app's CSS; without these it used its own grey on both themes.
const CHART_THEME = {
  light: { text: "#4b5563", grid: "rgba(0,0,0,0.08)" },
  dark: { text: "#c9d1d9", grid: "rgba(255,255,255,0.10)" },
} as const;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// The agent's own options win over the theme defaults, key by key.
function mergeOptions(base: Record<string, unknown>, over: unknown): Record<string, unknown> {
  if (!isPlainObject(over)) return base;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(over)) {
    out[k] = isPlainObject(v) && isPlainObject(base[k]) ? mergeOptions(base[k] as Record<string, unknown>, v) : v;
  }
  return out;
}

// A bad chart used to be able to take the whole screen down: only JSON parse
// errors were caught, and the app has no other error boundary.
class ChartErrorBoundary extends Component<
  { fallback: (message: string) => ReactNode; children: ReactNode },
  { message: string | null }
> {
  state = { message: null as string | null };
  static getDerivedStateFromError(error: unknown) {
    return { message: error instanceof Error ? error.message : "The chart could not be drawn." };
  }
  render() {
    return this.state.message !== null ? this.props.fallback(this.state.message) : this.props.children;
  }
}

function ChartProblem({ language, json, message }: { language: string; json: string; message: string }) {
  return (
    <div className="my-3">
      <div className="rounded-t-lg border border-b-0 border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
        <p className="font-medium">This chart has a mistake, so it could not be drawn. Its data is below.</p>
        <p className="mt-0.5 font-mono text-[11px] opacity-80">{message}</p>
      </div>
      <div className="-mt-3 [&>div]:rounded-t-none">
        <CodeBlock className={`language-${language}`}>{json}</CodeBlock>
      </div>
    </div>
  );
}

function InlineChart({ language, json }: { language: string; json: string }) {
  const { resolvedTheme } = useTheme();
  const colours = CHART_THEME[resolvedTheme];

  const parsed = useMemo(() => {
    try {
      const value = JSON.parse(json);
      if (!isPlainObject(value) || !Array.isArray(value.datasets)) {
        return { error: "It needs a \"datasets\" list." } as const;
      }
      return { value } as const;
    } catch {
      return { error: "Its data is not valid JSON." } as const;
    }
  }, [json]);

  if ("error" in parsed) return <ChartProblem language={language} json={json} message={parsed.error ?? ""} />;

  const { labels, datasets, options: userOptions } = parsed.value as {
    labels?: unknown;
    datasets: unknown[];
    options?: unknown;
  };
  const data = { labels, datasets } as never;
  const hasAxes = language === "chart-bar" || language === "chart-line";
  const themed: Record<string, unknown> = {
    responsive: true,
    // Fill the fixed-height box below. With the aspect ratio kept, a pie in a
    // wide message grew taller than its box and spilled out.
    maintainAspectRatio: false,
    color: colours.text,
    plugins: {
      legend: { labels: { color: colours.text } },
      title: { color: colours.text },
    },
    ...(hasAxes && {
      scales: {
        x: { ticks: { color: colours.text }, grid: { color: colours.grid } },
        y: { ticks: { color: colours.text }, grid: { color: colours.grid } },
      },
    }),
  };
  const options = mergeOptions(themed, userOptions) as never;

  const chartEl = (() => {
    switch (language) {
      case "chart-pie": return <Pie data={data} options={options} />;
      case "chart-bar": return <Bar data={data} options={options} />;
      case "chart-doughnut": return <Doughnut data={data} options={options} />;
      case "chart-line": return <Line data={data} options={options} />;
      default: return null;
    }
  })();
  if (!chartEl) return null;

  return (
    <ChartErrorBoundary
      key={`${json}-${resolvedTheme}`}
      fallback={(message) => <ChartProblem language={language} json={json} message={message} />}
    >
      <div className="relative my-3 h-[320px] rounded-lg border border-border bg-background p-4">{chartEl}</div>
    </ChartErrorBoundary>
  );
}

// ── Mermaid diagram component ──────────────────────────────────────
// Valid mermaid diagram types — used to pre-validate before attempting render.
// Includes the Mermaid 11 types (C4, requirement, architecture, kanban, packet,
// radar, treemap) that used to fall through to plain code.
const MERMAID_KEYWORDS = /^\s*(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|gitGraph|journey|mindmap|timeline|quadrantChart|sankey|xychart|block|C4Context|C4Container|C4Component|C4Dynamic|C4Deployment|requirementDiagram|architecture|kanban|packet|radar|treemap|zenuml)/;

// A diagram may open with an init directive (%%{init: ...}%%), YAML front
// matter (--- ... ---) or %% comments before its type keyword. Skip them so the
// keyword check sees the real first line.
function mermaidBody(code: string): string {
  return code
    .replace(/^\s*---\n[\s\S]*?\n---\s*\n/, "")
    .replace(/^(\s*%%.*\n)+/, "");
}

// First line or two of a mermaid parse error, which otherwise runs to a dozen
// lines of parser internals.
function shortMermaidError(message: string): string {
  return message.split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 2).join(" ");
}

// Mermaid uses shared parser and DOM state internally. A live reply normally
// renders one diagram at a time, while reopening a chat can mount several saved
// diagrams together. Serialize those renders so history behaves like the live
// view instead of intermittently falling back to the source code block.
let mermaidRenderQueue: Promise<void> = Promise.resolve();
async function renderMermaidDiagram(
  id: string,
  code: string,
  appTheme: "light" | "dark",
  isCancelled: () => boolean,
): Promise<string | null> {
  const render = async () => {
    // Streaming can replace the code while this render is waiting behind a
    // saved diagram. Skip obsolete work instead of building a render backlog.
    if (isCancelled()) return null;
    const mermaid = (await import("mermaid")).default;
    // Set the theme on every render (renders are queued, so this cannot race).
    // It used to be initialised once as "dark", so light mode got a dark
    // diagram on a light background.
    mermaid.initialize({
      startOnLoad: false,
      theme: appTheme === "dark" ? "dark" : "default",
      securityLevel: "strict",
    });
    const { svg } = await mermaid.render(id, code);
    return svg;
  };

  const pending = mermaidRenderQueue.then(render, render);
  mermaidRenderQueue = pending.then(() => undefined, () => undefined);
  return pending;
}

function MermaidDiagram({ code }: { code: string }) {
  const { resolvedTheme } = useTheme();
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"diagram" | "code">("diagram");
  const [copied, setCopied] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Streaming updates reuse this component. Do not let a result from an
    // earlier, partial version of the diagram decide the final UI state.
    setSvg(null);
    setError(null);

    // Quick pre-validation: only attempt render if the code starts with a known diagram keyword
    if (!MERMAID_KEYWORDS.test(mermaidBody(code))) {
      setError("It does not start with a diagram type Mermaid recognises.");
      return;
    }

    (async () => {
      const id = `mermaid-${Math.random().toString(36).slice(2, 10)}`;
      try {
        const rendered = await renderMermaidDiagram(id, code, resolvedTheme, () => cancelled);
        if (!cancelled && rendered) setSvg(rendered);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Mermaid render failed");
      } finally {
        // Mermaid prefixes its temporary error element with "d". Clean up
        // this render only, including when navigation unmounts the component.
        document.getElementById(`d${id}`)?.remove();
      }
    })();
    return () => { cancelled = true; };
    // Redrawn when the app switches between light and dark.
  }, [code, resolvedTheme]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadSvg = () => {
    if (!svg) return;
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "diagram.svg";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  // Say plainly that the diagram could not be drawn, then show the code. It used
  // to fall back to the code with no explanation, which read as the chat
  // ignoring the diagram.
  if (error) {
    return (
      <div className="my-3">
        <div className="rounded-t-lg border border-b-0 border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">
          <p className="font-medium">This diagram has a mistake, so it could not be drawn. Its code is below.</p>
          <p className="mt-0.5 font-mono text-[11px] opacity-80">{shortMermaidError(error)}</p>
        </div>
        <div className="-mt-3 [&>div]:rounded-t-none">
          <CodeBlock className="language-mermaid">{code}</CodeBlock>
        </div>
      </div>
    );
  }
  if (!svg) return <MermaidPlaceholder />;

  // Width divided by height, from the SVG's viewBox, for the full-screen size.
  const viewBox = /viewBox="[\d.\-]+\s+[\d.\-]+\s+([\d.]+)\s+([\d.]+)"/.exec(svg);
  const svgAspect = viewBox && Number(viewBox[2]) > 0 ? Number(viewBox[1]) / Number(viewBox[2]) : 4 / 3;

  const tabClass = (active: boolean) =>
    cn(
      "rounded px-1.5 py-0.5 transition-colors",
      active ? "bg-surface text-text-primary" : "hover:text-text-primary",
    );

  // Header bar in the same style as a code block: the label on the left, the
  // Diagram / Code switch and the actions on the right.
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-border">
      <div className="flex items-center justify-between border-b border-border bg-surface-hover px-3 py-1.5 text-[11px] text-text-muted">
        <div className="flex items-center gap-1" role="tablist" aria-label="Diagram view">
          <button role="tab" aria-selected={view === "diagram"} onClick={() => setView("diagram")} className={tabClass(view === "diagram")}>
            Diagram
          </button>
          <button role="tab" aria-selected={view === "code"} onClick={() => setView("code")} className={tabClass(view === "code")}>
            Code
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCopy}
            aria-label="Copy diagram code"
            title="Copy the diagram's code"
            className="flex items-center gap-1 transition-colors hover:text-text-primary"
          >
            {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            <span>{copied ? "Copied" : "Copy"}</span>
          </button>
          <button
            onClick={handleDownloadSvg}
            aria-label="Download diagram as SVG"
            title="Download as an SVG picture"
            className="flex items-center gap-1 transition-colors hover:text-text-primary"
          >
            <Download size={12} />
            <span>SVG</span>
          </button>
          <button
            onClick={() => setFullScreen(true)}
            aria-label="View diagram full screen"
            title="Full screen, with zoom"
            className="flex items-center gap-1 transition-colors hover:text-text-primary"
          >
            <Maximize2 size={12} />
            <span>Full screen</span>
          </button>
        </div>
      </div>
      <ZoomViewer open={fullScreen} onClose={() => setFullScreen(false)} title="Diagram">
        {/* Sized to the diagram's own shape: as large as fits in 90% of the
            width and 80% of the height, keeping its proportions. A fixed box
            left a tall diagram small in the middle of a wide grey panel. */}
        <div
          className="rounded-lg bg-background p-6 [&>svg]:!h-full [&>svg]:!w-full [&>svg]:!max-w-none"
          style={{
            aspectRatio: `${svgAspect}`,
            width: `min(90vw, calc(80vh * ${svgAspect}))`,
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </ZoomViewer>
      {view === "diagram" ? (
        <div
          // The box spans the full reply width; the diagram sits centred in it
          // rather than stuck to the left edge.
          className="overflow-x-auto bg-background p-4 [&>svg]:mx-auto [&>svg]:block [&>svg]:h-auto [&>svg]:max-w-full"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <pre className="!m-0 max-w-full overflow-x-auto whitespace-pre bg-[#0d1117] p-3 text-xs leading-relaxed">
          <code className="font-mono text-xs leading-relaxed text-[#e6edf3]">{code}</code>
        </pre>
      )}
    </div>
  );
}

// Shown while a diagram is still arriving or being drawn. One steady box, so a
// half-written diagram never flashes an error or its raw code.
function MermaidPlaceholder({ label = "Drawing diagram…" }: { label?: string }) {
  return (
    <div className="my-3 rounded-lg border border-border p-4 text-xs text-text-muted animate-pulse">
      {label}
    </div>
  );
}

// ── Sandboxed HTML artifact ────────────────────────────────────────
function HtmlArtifact({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const iframeHeight = expanded ? 700 : 400;

  // Sandboxed, like the inline preview. A plain blob: URL ran the agent's
  // scripts with ProClaw's origin and the user's session.
  const openInNewTab = useCallback(() => openHtmlInSandboxedTab(html, "HTML Visualisation"), [html]);

  return (
    <div className="my-3 rounded-lg border border-border overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-surface-hover px-3 py-1.5 text-[11px] text-text-muted border-b border-border">
        <span className="font-medium">Interactive Visualisation</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSource(!showSource)} className="flex items-center gap-1 hover:text-text-primary transition-colors">
            <Code2 size={12} />
            <span>{showSource ? "Hide Source" : "View Source"}</span>
          </button>
          <button onClick={openInNewTab} className="flex items-center gap-1 hover:text-text-primary transition-colors">
            <NewTab size={12} />
            <span>Open in new tab</span>
          </button>
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 hover:text-text-primary transition-colors">
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            <span>{expanded ? "Collapse" : "Expand"}</span>
          </button>
        </div>
      </div>
      {/* Source view */}
      {showSource && (
        <pre className="overflow-x-auto bg-surface p-3 text-xs text-text-secondary border-b border-border max-h-[300px]">
          <code>{html}</code>
        </pre>
      )}
      {/* Sandboxed iframe */}
      <iframe
        srcDoc={html}
        // allow-modals lets alert/confirm work (they were silently blocked);
        // still no allow-same-origin, so the page has no access to ProClaw.
        sandbox="allow-scripts allow-modals"
        className="w-full border-0 bg-white"
        style={{ height: iframeHeight }}
        title="HTML Visualisation"
      />
    </div>
  );
}

// ── Code block (original) ──────────────────────────────────────────

// The plain text inside rendered children. rehype-highlight turns a code
// block's text into nested <span>s for any language it knows, so
// String(children) gave "[object Object]" and that is what Copy put on the
// clipboard. Walk the tree and join the text instead.
function nodeText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) return nodeText(node.props.children);
  return "";
}

// Code blocks longer than this start folded (the fold is ~25 lines tall).
const LONG_CODE_LINES = 30;

function CodeBlock({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || "");
  const language = match?.[1] || "";
  const code = nodeText(children).replace(/\n$/, "");
  const lineCount = code.split("\n").length;
  // Long blocks start folded to about 25 lines, so a 500-line script no
  // longer fills the whole chat. Copy always copies all of it.
  const isLong = lineCount > LONG_CODE_LINES;
  const [expanded, setExpanded] = useState(false);
  const folded = isLong && !expanded;

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group relative my-3 max-w-full overflow-hidden rounded-lg border border-border">
      <div className="flex items-center justify-between bg-surface-hover px-3 py-1.5 text-[11px] text-text-muted border-b border-border">
        <span className="font-mono">{language || "code"}</span>
        <button
          onClick={handleCopy}
          aria-label="Copy code block"
          title="Copy code block"
          className="flex items-center gap-1 text-text-muted hover:text-text-primary transition-colors"
        >
          {copied ? (
            <>
              <Check size={12} className="text-emerald-400" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy size={12} />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <div className="relative">
        <pre
          className={cn(
            "!m-0 max-w-full overflow-x-auto whitespace-pre bg-[#0d1117] p-3 text-xs leading-relaxed",
            folded && "max-h-[420px] overflow-y-hidden",
          )}
        >
          <code className={cnCodeClass(className)}>{children}</code>
        </pre>
        {folded && (
          // Fade at the fold, so it reads as "more below" rather than cut off.
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#0d1117] to-transparent" />
        )}
      </div>
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-center gap-1 border-t border-border bg-surface-hover px-3 py-1.5 text-[11px] text-text-muted transition-colors hover:text-text-primary"
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? "Show less" : `Show all ${lineCount} lines`}
        </button>
      )}
    </div>
  );
}

function cnCodeClass(className?: string): string {
  return [
    className,
    "font-mono whitespace-pre text-xs leading-relaxed",
  ].filter(Boolean).join(" ");
}

// ── Image in a reply: fits the message, click for full screen ──────
// There was no image renderer, so a wide picture ran past the message and was
// cut off by the chat's hidden overflow.
function ChatImage({ src, alt }: { src: string; alt: string }) {
  // A picture that cannot load (gone, blocked, needs a sign-in) showed the
  // browser's broken-image icon with the alt text beside it. Say so plainly
  // and keep the address, so the person can try it themselves.
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="my-2 flex max-w-md items-start gap-2.5 rounded-lg border border-border bg-surface px-3 py-2.5">
        <ImageOff size={16} className="mt-0.5 shrink-0 text-text-muted" />
        <div className="min-w-0">
          <p className="m-0 text-[13px] font-medium text-text-primary">Image Could Not Load</p>
          {alt && <p className="m-0 truncate text-xs text-text-muted">{alt}</p>}
          {/^https?:/i.test(src) && (
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-0.5 inline-flex max-w-full items-center gap-1 text-xs text-accent underline underline-offset-2 hover:text-accent-hover"
            >
              <span className="truncate">{src}</span>
              <ExternalLink size={10} className="shrink-0" />
            </a>
          )}
        </div>
      </div>
    );
  }
  return (
    <ZoomableImage
      onError={() => setFailed(true)}
      src={src}
      alt={alt}
      buttonClassName="my-2 block max-w-full cursor-zoom-in"
      className="max-h-[480px] max-w-full rounded-lg border border-border object-contain"
    />
  );
}

// ── Table with copy and CSV download ────────────────────────────────

// The table as rows of cell text, read from the rendered table so it matches
// exactly what is on screen (links and formatting reduced to their text).
function tableRows(table: HTMLTableElement): string[][] {
  return Array.from(table.rows).map((row) =>
    Array.from(row.cells).map((cell) => (cell.textContent ?? "").replace(/\s+/g, " ").trim()),
  );
}

// A file name for a downloaded table, with no AI call: the nearest heading the
// agent wrote above the table ("UK Offices" → uk-offices.csv), otherwise the
// first few column names, otherwise "table".
function tableFileName(wrapper: HTMLElement | null, rows: string[][]): string {
  const slug = (text: string) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60).replace(/-+$/, "");
  let el = wrapper?.previousElementSibling ?? null;
  for (let i = 0; el && i < 6; i++, el = el.previousElementSibling) {
    if (/^H[1-4]$/.test(el.tagName)) {
      const name = slug(el.textContent ?? "");
      if (name) return `${name}.csv`;
      break;
    }
  }
  const fromColumns = slug((rows[0] ?? []).slice(0, 4).join(" "));
  return `${fromColumns || "table"}.csv`;
}

function toCsv(rows: string[][]): string {
  const cell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return rows.map((r) => r.map(cell).join(",")).join("\r\n");
}

function ChatTable({ children }: { children?: ReactNode }) {
  const tableRef = useRef<HTMLTableElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  // Tab-separated, which pastes into Excel and Google Sheets as proper cells.
  const handleCopy = () => {
    if (!tableRef.current) return;
    const tsv = tableRows(tableRef.current).map((r) => r.join("\t")).join("\n");
    navigator.clipboard.writeText(tsv);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!tableRef.current) return;
    // The byte-order mark makes Excel read the file as UTF-8, so £, € and
    // accented names survive.
    const rows = tableRows(tableRef.current);
    const blob = new Blob(["﻿" + toCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = tableFileName(wrapperRef.current, rows);
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  return (
    <div ref={wrapperRef} className="my-3 overflow-hidden rounded-lg border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-[11px] text-text-muted">
        <span className="font-mono">table</span>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCopy}
            aria-label="Copy table"
            title="Copy table (pastes into Excel as cells)"
            className="flex items-center gap-1 transition-colors hover:text-text-primary"
          >
            {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            <span>{copied ? "Copied" : "Copy"}</span>
          </button>
          <button
            onClick={handleDownload}
            aria-label="Download table as CSV"
            title="Download as a CSV file"
            className="flex items-center gap-1 transition-colors hover:text-text-primary"
          >
            <Download size={12} />
            <span>CSV</span>
          </button>
        </div>
      </div>
      {/* Scrolls both ways inside its own box: sideways for wide tables, and
          down for long ones so the sticky header has something to stick to. */}
      <div className="max-h-[480px] overflow-auto">
        <table ref={tableRef} className="min-w-full lg:w-full text-sm">{children}</table>
      </div>
    </div>
  );
}

// Whether a fenced block's closing ``` (or ~~~) has arrived. `position` is the
// block's span in the markdown source, fence lines included; an unclosed fence
// runs to the end of what has streamed so far and so does not end in a fence.
function isFenceClosed(
  content: string,
  position: { start: { offset?: number }; end: { offset?: number } } | undefined,
): boolean {
  const start = position?.start.offset;
  const end = position?.end.offset;
  if (start === undefined || end === undefined) return true;
  return /\n[ \t]*(`{3,}|~{3,})[ \t]*$/.test(content.slice(start, end));
}

function InlineCode({ children }: { children?: ReactNode }) {
  return (
    <code className="bg-surface-hover text-accent px-1.5 py-0.5 rounded text-[0.85em] font-mono">
      {children}
    </code>
  );
}

/*
 * ── Rich code block formats supported by agents ────────────────────
 *
 * Agents can output these fenced code blocks for inline rendering:
 *
 * ```chart-pie / chart-bar / chart-doughnut / chart-line
 * { "labels": [...], "datasets": [{ "data": [...], ... }], "options": {...} }
 * ```
 *
 * ```html-artifact / html-render
 * <html>...</html>
 * ```
 *
 * ```mermaid
 * graph TD; A-->B; B-->C;
 * ```
 */

/**
 * An id in a reply. When it is one of this company's workflows it shows as the
 * workflow's name, underlined, linking to its canvas: a shortcut instead of an
 * id to copy. Anything else (an agent or run id, or no company in the address)
 * shows exactly as the agent wrote it.
 *
 * The company's workflow list is usually cached already (the Workflows screen
 * and Scout use the same query). An id missing from it, such as a workflow the
 * agent made in this very reply, is looked up on its own once.
 */
/**
 * Lets a reply's own file links open the file panel. The chat message that
 * renders the reply knows which agent wrote it, so it provides this; other
 * places that render markdown (Scout, Goals) do not, and a file link there
 * shows as plain text.
 */
export const FileLinkContext = createContext<{ openFile?: (file: Artifact) => void }>({});

/**
 * A link an agent wrote to a file it made, such as ChatGPT's
 * `sandbox:/mnt/data/report.docx`. A browser cannot follow those, so the link
 * was left with an empty address that only reloaded the page. It opens the
 * same file panel as the Open button under the reply.
 */
function AgentFileLink({ file, children }: { file: Artifact; children: ReactNode }) {
  const { openFile } = useContext(FileLinkContext);
  if (!openFile) {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-text-primary">
        <FileText size={12} className="shrink-0 text-text-muted" />
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => openFile(file)}
      title={`Open ${file.title}`}
      className="inline-flex items-center gap-1 text-accent underline underline-offset-2 hover:text-accent-hover"
    >
      <FileText size={12} className="shrink-0" />
      {children}
    </button>
  );
}

/** Keep a file link's address so the link component can open it; anything else goes through the usual safety filter. */
function chatUrlTransform(url: string): string {
  return isAgentFileLink(url) ? url : defaultUrlTransform(url);
}

function IdLink({ id, writtenAs }: { id: string; writtenAs: string }) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [searchParams] = useSearchParams();
  const { data: list } = useQuery({
    queryKey: queryKeys.workflows.byCompany(tenantId ?? ""),
    queryFn: () => workflowsApi.list(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
  const listed = list?.find((w) => w.id === id);
  const { data: fetched } = useQuery({
    queryKey: queryKeys.workflows.detail(id),
    queryFn: () => workflowsApi.get(id),
    enabled: !!tenantId && !!list && !listed,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const name = listed?.name ?? (fetched && fetched.companyId === tenantId ? fetched.name : undefined);

  // Agents often write the name and then "ID: <id>" on the same item, so the
  // link repeated the name ("Ticket triage / ID: Ticket triage"). When the id
  // follows an "ID:" label and the name is already written in the same list
  // item or paragraph, say "Open". Only after a label: in a sentence ("the
  // X workflow is <id>") an "Open" would read as the workflow's status.
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [repeated, setRepeated] = useState(false);
  useLayoutEffect(() => {
    const el = linkRef.current;
    const block = el?.closest("li, p, td");
    if (!el || !block || !name) return;
    const afterIdLabel = /\bID\s*[:-]?\s*$/i.test(el.previousSibling?.textContent ?? "");
    setRepeated(afterIdLabel && (block.textContent ?? "").split(name).length - 1 > 1);
  }, [name]);

  if (!tenantId || !name) {
    return writtenAs === "code" ? <InlineCode>{id}</InlineCode> : <>{id}</>;
  }
  return (
    <Link
      to={withWorkspaceContext(`/tenants/${tenantId}/workflows/${id}`, searchParams)}
      ref={linkRef}
      title={repeated ? `Open ${name}` : "Open this workflow"}
      className="inline-flex items-center gap-0.5 font-medium text-accent underline underline-offset-2 hover:text-accent-hover"
    >
      {repeated ? "Open" : name}
      {repeated && <ExternalLink size={10} className="shrink-0" />}
    </Link>
  );
}

// Memoized to prevent re-parsing unchanged content during streaming
export const MarkdownRenderer = memo(function MarkdownRenderer({
  content,
  streaming = false,
}: {
  content: string;
  /** The reply is still arriving. A diagram whose closing ``` has not come in
   *  yet shows a placeholder instead of being drawn from half its code. */
  streaming?: boolean;
}) {
  // Memoize plugins arrays so they're referentially stable
  const remarkPlugins = useMemo(() => [remarkGfm, remarkUuidLinks], []);
  // PowerShell is not in lowlight common set, so PS scripts fell back to
  // untokenized plaintext. Register it (plus aliases) on top of common.
  const rehypePlugins = useMemo<PluggableList>(
    () => [[rehypeHighlight, { languages: { ...common, powershell }, aliases: { powershell: ["ps", "ps1", "pwsh"] } }]],
    [],
  );

  return (
    <ReactMarkdown
      urlTransform={chatUrlTransform}
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={{
        code({ className, children, node, ...props }) {
          const isBlock =
            className?.includes("language-") ||
            (typeof children === "string" && children.includes("\n"));

          if (isBlock) {
            const langMatch = /language-(\S+)/.exec(className || "");
            const lang = langMatch?.[1] || "";
            const code = String(children).replace(/\n$/, "");

            // Chart.js inline charts. Same rule as diagrams: half-arrived JSON
            // would read as a broken chart, so wait for the closing fence.
            if (CHART_LANGUAGES.has(lang)) {
              if (streaming && !isFenceClosed(content, node?.position)) {
                return <MermaidPlaceholder label="Drawing chart…" />;
              }
              return <InlineChart language={lang} json={code} />;
            }

            // Sandboxed HTML artifacts
            if (lang === "html-artifact" || lang === "html-render") {
              return <HtmlArtifact html={code} />;
            }

            // Mermaid diagrams. While the reply streams, an unclosed fence is
            // still parsed as a code block, and drawing each partial version
            // made the diagram flicker between "drawing", an error and its
            // code. Wait for the closing fence.
            if (lang === "mermaid") {
              if (streaming && !isFenceClosed(content, node?.position)) {
                return <MermaidPlaceholder />;
              }
              return <MermaidDiagram code={code} />;
            }

            return <CodeBlock className={className}>{children}</CodeBlock>;
          }
          return <InlineCode>{children}</InlineCode>;
        },
        table({ children }) {
          return <ChatTable>{children}</ChatTable>;
        },
        thead({ children }) {
          return <thead className="text-text-secondary text-xs uppercase tracking-wide">{children}</thead>;
        },
        th({ children }) {
          // Sticky inside the table's own scroll box, so the column names stay
          // visible down a long table. The background sits on the cell because
          // a sticky cell with no background lets rows show through it.
          return <th className="sticky top-0 z-[1] bg-surface-hover px-3 py-2 lg:px-3.5 lg:py-2.5 text-left text-[12px] font-medium uppercase tracking-wider border-b border-border">{children}</th>;
        },
        td({ children }) {
          return <td className="px-3 py-2 lg:px-3.5 lg:py-2.5 border-b border-border">{children}</td>;
        },
        img({ src, alt }) {
          return typeof src === "string" ? <ChatImage src={src} alt={alt ?? ""} /> : null;
        },
        blockquote({ children }) {
          return (
            <blockquote className="border-l-3 border-accent/40 bg-accent/5 pl-4 py-2 my-3 rounded-r-lg text-text-secondary">
              {children}
            </blockquote>
          );
        },
        a({ href, title, children }) {
          // Ids marked by remarkUuidLinks: a workflow's name, or the id as written.
          if (href?.startsWith(UUID_LINK_PREFIX)) {
            return <IdLink id={href.slice(UUID_LINK_PREFIX.length)} writtenAs={title ?? "text"} />;
          }
          // A file the agent made, linked in a way a browser cannot open.
          const file = href && isAgentFileLink(href) ? fileRefFromLink(href) : null;
          if (file) return <AgentFileLink file={file}>{children}</AgentFileLink>;
          if (href && isAgentFileLink(href)) return <>{children}</>;
          const isExternal = href?.startsWith("http");
          return (
            <a
              href={href}
              target={isExternal ? "_blank" : undefined}
              rel={isExternal ? "noopener noreferrer" : undefined}
              className="text-accent hover:text-accent-hover underline underline-offset-2 inline-flex items-center gap-0.5"
            >
              {children}
              {isExternal && <ExternalLink size={10} className="shrink-0" />}
            </a>
          );
        },
        hr() {
          return <hr className="my-4 border-border" />;
        },
        ul({ children }) {
          return <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>;
        },
        ol({ children }) {
          return <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>;
        },
        li({ children }) {
          return <li className="text-text-primary">{children}</li>;
        },
        p({ children }) {
          return <p className="my-2 leading-relaxed lg:text-[14px] lg:leading-[1.65]">{children}</p>;
        },
        h1({ children }) {
          return <h1 className="text-lg font-bold mt-4 mb-2">{children}</h1>;
        },
        h2({ children }) {
          return <h2 className="text-base font-bold mt-4 mb-2">{children}</h2>;
        },
        h3({ children }) {
          return <h3 className="text-sm font-semibold mt-3 mb-1.5">{children}</h3>;
        },
        h4({ children }) {
          return <h4 className="text-sm font-medium mt-2 mb-1">{children}</h4>;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
});
